import { forwardRef, useState, useEffect } from 'react';
import clsx from 'clsx';

import { Input } from '~/shared/components/Input';
import { sizeUnitMap, sizeApiUnitMap } from '~/settings/utils/configValueParser';
import Typography from '~/shared/components/Typography';

interface SizeInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  className?: string;
  disabled?: boolean;
  unit?: string; // API unit format (e.g., 'gb', 'mb')
  min?: number;
  max?: number;
}

const SizeInput = forwardRef<HTMLInputElement, SizeInputProps>(
  ({ value, onChange, error, className, disabled, unit = 'gb', min, max }, ref) => {
    const [numericValue, setNumericValue] = useState('');
    const displayUnit = sizeUnitMap[unit] || 'GiB';

    // Parse incoming value
    useEffect(() => {
      if (value) {
        const sizeRegex = /^(\d+(?:\.\d+)?)\s*([a-z]+)?$/i;
        const match = value.match(sizeRegex);

        if (match) {
          setNumericValue(match[1]);
        }
      }
    }, [value]);

    const handleValueChange = (newValue: string) => {
      setNumericValue(newValue);
      onChange(`${newValue}${unit}`);
    };

    return (
      <Input
        ref={ref}
        type="number"
        value={numericValue}
        onChange={(e) => handleValueChange(e.target.value)}
        error={error}
        disabled={disabled}
        wrapperClassName={clsx('max-w-[160px]', className)}
        inputClassName="px-1!"
        step="any"
        min={min}
        max={max}
        rightAdornment={
          <Typography variant="p3" color="text-gray-700">
            {displayUnit}
          </Typography>
        }
        rightAdornmentClassName="right-1!"
      />
    );
  }
);

SizeInput.displayName = 'SizeInput';

export default SizeInput;
